import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Dialog from "@/components/common/Dialog";
import { Button } from "../ui/button";
import Loader from "../common/Loader";
import ExcelIcon from "@/assets/v2/svg/excel.svg";
import DownLoadIcon from "@/assets/svg/DownLoad.svg";
import crossNew from "@/assets/svg/crossNew.svg";
import useHierarchyImportMutation from "@/api/mutation/useHierarchyImportMutation";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { errorConfig } from "@/lib/toastError";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/api/query/querykeys";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  downloadTemplate: () => void;
};

export default function HierarchyImportModal({ isOpen, onClose, onSuccess, downloadTemplate }: Props) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useHierarchyImportMutation();
  const queryClient = useQueryClient();

  const handleUpload = () => {
    if (fileInputRef.current?.files?.[0]) {
      const file = fileInputRef.current.files[0];
      const formData = new FormData();
      formData.append("csv", file);

      importMutation.mutate(formData, {
        onSuccess() {
          toast.success(t("Import successful!"));
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] });
          onSuccess();
          onClose();
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message || "Failed to import hierarchy"),
            errorConfig
          );
        },
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) {
      const file = e.dataTransfer.files[0];
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
        handleUpload();
      }
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} modalClass="rounded-2xl max-w-md w-full bg-white p-6 shadow-xl border border-gray-150">
      <div>
        <div className="pb-3 border-b border-gray-100 relative">
          <h2 className="text-lg font-bold text-gray-800">
            {t("Import Portfolio / Program / Projects")}
          </h2>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed pr-8">
            {t("Upload a structured Excel sheet containing the projects hierarchy mapping.")}
          </p>
          <button 
            onClick={onClose} 
            className="absolute top-0 right-0 p-1.5 rounded-full hover:bg-gray-100/80 transition-colors z-50 cursor-pointer"
          >
            <img src={crossNew} className="w-3.5 h-3.5 opacity-60 hover:opacity-90 brightness-0" alt="Close" />
          </button>
        </div>

        <div className="relative mt-4 space-y-4">
          {importMutation.isPending && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50 rounded-xl">
              <Loader />
            </div>
          )}

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-dashed border-2 border-gray-200 hover:border-[#535EF9] hover:bg-blue-50/5 rounded-xl cursor-pointer p-8 flex flex-col items-center justify-center gap-3 transition-all group bg-white"
          >
            <div className="p-3 bg-blue-50 rounded-full text-[#535EF9] group-hover:scale-110 transition-transform duration-200">
              <img src={ExcelIcon} className="w-8 h-8" alt="Excel" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm font-semibold text-gray-700">
                {t("wbs_drag_drop_title")}
              </p>
              <p className="text-xs text-gray-400">
                {t("wbs_drag_drop_subtitle")}
              </p>
            </div>
          </div>

          <input
            onChange={handleUpload}
            type="file"
            accept=".xlsx"
            className="hidden"
            ref={fileInputRef}
          />

          <div className="flex flex-col items-center justify-center gap-2 pt-2">
            <Button
              variant="link"
              className="text-xs text-gray-500 hover:text-[#535EF9] hover:underline flex items-center gap-1.5 py-1 px-3 bg-gray-50 border border-gray-200 rounded-lg shadow-2xs transition-colors"
              type="button"
              onClick={downloadTemplate}
            >
              <img className="h-3.5 w-3.5" src={DownLoadIcon} alt="Download" />
              {t("Download_sample_File")}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
